import { bindable, View, ViewEngine } from "aurelia-templating";
import { autoinject, transient } from "aurelia-dependency-injection";
import * as Enumerable from "linq";
import { Messages } from "../../../configuration/event-messages.constants";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { BindingEngine, computedFrom } from "aurelia-binding";
import { MobileHelper } from "../../../helpers/mobile-helper";
import { Reflection } from "../../../utilities/reflection";
import { KeyCode } from "../../../models/basics/basics.models";
import { Container } from "aurelia-framework";

@autoinject
@transient()
export class ComboBox {

    // private Properties
    private _indexFocusedLi: number = 0;
    private _subscriptionToDocumentClickEvent: Subscription;
    private _scrollViewerTop: number;
    private _invertDropdown: boolean;
    private _isAttached: boolean;
    private _itemsToHideObserver: Subscription;

    @computedFrom(Reflection.getPropertyPath<ComboBox>(z => z.isMobile), Reflection.getPropertyPath<ComboBox>(z => z.showOnAdorner),
        Reflection.getPropertyPath<ComboBox>(z => z.inputFilter), Reflection.getPropertyPath<ComboBox>(z => z.inputCombo))
    public get input(): HTMLInputElement {
        return this.isMobile && this.showOnAdorner ? this.inputFilter : this.inputCombo; 
    }
    
    public inputFilter: HTMLInputElement;
    public inputCombo: HTMLInputElement;
    public dropdownElement: any;
    public filteredItemSource: any[];
    public isMobile: boolean = MobileHelper.isMobileDevice();
    public dropDownContext: DropDownContext;
    public dropdown: View;

    @bindable
    public identifier: string;

    @bindable
    public displayMemberPath: string | ((item) => string);

    @bindable
    public isEditing: boolean;

    @bindable
    public searchValue: string;

    @bindable
    public selectedValuePath: string;

    @bindable
    public itemSource: any[];

    @bindable
    public readonly: boolean;

    @bindable
    public disabled: boolean;

    @bindable
    public selectedItem: any;

    @bindable
    public selectedValue: any;

    @bindable
    public placeholder: string = "";

    @bindable
    public label: string;

    @bindable
    public info: string = "";

    @bindable
    public infoBottom: string = "";

    @bindable
    public itemsToHide: any[] = [];

    @bindable
    public setDefault: boolean | ((itemSource: any[]) => any);

    @bindable
    public isDropdownOpen: boolean;

    @bindable
    public showOnAdorner: boolean = true;

    @bindable
    public isSearchable: boolean = true;

    @bindable
    public isTextHtml: boolean = false;

    @bindable
    public required: boolean = false;

    @bindable
    public theme: string = "primary";

    itemsToHideChanged(newValue) {
        if (this._itemsToHideObserver) {
            this._itemsToHideObserver.dispose();
            this._itemsToHideObserver = null;
        }

        if (newValue) {
            this.bindingEngine.collectionObserver(newValue).subscribe(() => {
                this.filteredItemSource = this.internalItemSource.toArray();
            });
        }
        this.filteredItemSource = this.internalItemSource.toArray();
    }

    @computedFrom(Reflection.getPropertyPath<ComboBox>(z => z.dropdownElement), Reflection.getPropertyPath<ComboBox>(z => z.dropdownElement.clientHeight),
        Reflection.getPropertyPath<ComboBox>(z => z._scrollViewerTop))
    public get invertDropdown(): boolean {
        if (this.dropdownElement) {
            const scrollViewer = this.getScrollParent(this.dropdownElement);
            if (scrollViewer) {
                if (!this._scrollViewerTop) {
                    this._scrollViewerTop = this.getTop(scrollViewer);
                }

                const dropdownPosition = ((this.getTop($(this.dropdownElement).parent().get(0)) + $(this.dropdownElement).parent().get(0).clientHeight) - this._scrollViewerTop) + this.dropdownElement.clientHeight;

                if (scrollViewer.nodeType != document.DOCUMENT_FRAGMENT_NODE && scrollViewer.clientHeight) {
                    this._invertDropdown = scrollViewer.clientHeight < dropdownPosition;
                }
            }
        }
        return this._invertDropdown;
    }
 
    get internalItemSource() {
        if (this.itemsToHide?.length > 0 && this.itemSource) {
            const valuesToHide = this.selectedValuePath ? this.itemsToHide.map(x => x[this.selectedValuePath]) : this.itemsToHide;
            return Enumerable.from<any>(this.itemSource).where(x => valuesToHide.every(z => z != (this.selectedValuePath ? x[this.selectedValuePath] : x)));
        }
        return Enumerable.from<any>(this.itemSource);
    }

    @computedFrom(Reflection.getPropertyPath<ComboBox>(z => z._indexFocusedLi))
    public get indexFocusedLi() {
        return this._indexFocusedLi;
    }

    constructor(private element: Element, private eventAggregator: EventAggregator,
        private bindingEngine: BindingEngine,
        private viewEngine: ViewEngine) {
    }

    public attached(): void {
        if (this._isAttached) {
            return;
        }

        this._isAttached = true;
        this.dropDownContext = new DropDownContext(this);
        if (this.input) {
            this.input.addEventListener("keydown", this.onKeyDown, false);
        }
        this.selectedValueChanged(this.selectedValue, undefined);
        this.itemSourceChanged(this.itemSource, undefined);
        
        this._subscriptionToDocumentClickEvent = this.eventAggregator.subscribe(Messages.documentClick, this.closePopup);

        this.viewEngine.loadViewFactory('views/components/combo-box/combo-box-dropdown.html').then(response => {
            this.dropdown = response.create(Container.instance);
        });

        this.subscribeOnPageHidden();
    }

    subscribeOnPageHidden = () => {
        let visibilityChange = null;
        if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
            visibilityChange = "visibilitychange";
        } else if (typeof document["msHidden"] !== "undefined") {
            visibilityChange = "msvisibilitychange";
        } else if (typeof document["webkitHidden"] !== "undefined") {
            visibilityChange = "webkitvisibilitychange";
        }

        document.addEventListener(visibilityChange, this.onDocumentHidden, false);

        window.addEventListener('blur', this.onPageHidden, false);
    }

    unsubscribeOnPageHidden = () => {
        let visibilityChange = null;
        if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
            visibilityChange = "visibilitychange";
        } else if (typeof document["msHidden"] !== "undefined") {
            visibilityChange = "msvisibilitychange";
        } else if (typeof document["webkitHidden"] !== "undefined") {
            visibilityChange = "webkitvisibilitychange";
        }

        document.removeEventListener(visibilityChange, this.onDocumentHidden, false);

        window.removeEventListener('blur', this.onPageHidden, false);
    }

    onDocumentHidden = () => {
        let hidden = null;
        if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
            hidden = "hidden";
        } else if (typeof document["msHidden"] !== "undefined") {
            hidden = "msHidden";
        } else if (typeof document["webkitHidden"] !== "undefined") {
            hidden = "webkitHidden";
        }
        if (document[hidden] && this.input) {
            this.input.blur();
        }
    }

    onPageHidden = () => {
        if (this.input) {
            this.input.blur();
        }
    }

    public detached(): void {
        this._isAttached = false;
        if (this.input) {
            this.input.removeEventListener("keydown", this.onKeyDown, false);
        }
        if (this._subscriptionToDocumentClickEvent) {
            this._subscriptionToDocumentClickEvent.dispose();
        }
        if (this.dropDownContext) {
            this.dropDownContext.comboBoxContext = null;
            this.dropDownContext = null;
        }
        if (this._itemsToHideObserver) {
            this._itemsToHideObserver.dispose();
        }

        this.unsubscribeOnPageHidden();
    }

    public getScrollParent(node) {
        if (node === null) {
            return null;
        }

        if (this.hasScroll(node)) {
            return node;
        } else {
            return this.getScrollParent(node.parentNode);
        }
    }

    public hasScroll(el: any): boolean {
        if (el.nodeType != document.ELEMENT_NODE) {
            return true;
        }

        const $el = $(el),
            sY = $el.css("overflow-y"),
            hidden = "hidden", // minifiers would like this better
            visible = "visible",
            scroll = "scroll";

        if (sY === hidden || sY === visible) { return false; }
        if (sY === scroll) { return true; }

        // compare client and scroll dimensions to see if a scrollbar is needed

        return $el.innerHeight() < el.scrollHeight || // make use of potential short circuit
            $el.innerWidth() < el.scrollWidth;
    }

    public selectItemByIndex(index: number): void {
        this.select(this.filteredItemSource[index]);
    }

    public onKeyDownNavigation($event: KeyboardEvent): void {
        const code = $event.keyCode;
        if (code === KeyCode.Enter) {
            this.selectItemByIndex(this._indexFocusedLi);
        } else if (code === KeyCode.ArrowUp) {
            this._indexFocusedLi = Math.max(0, this._indexFocusedLi - 1);
        } else if (code === KeyCode.ArrowDown) {
            this._indexFocusedLi = Math.min(this.filteredItemSource.length - 1, this._indexFocusedLi + 1);
        }
    }

    public getDisplayName(item: any): string {
        if (this.displayMemberPath && item) {
            if (typeof this.displayMemberPath == "function") {
                return this.displayMemberPath(item);
            } else {
                if (this.displayMemberPath.indexOf(".") > -1) {
                    const splitted = this.displayMemberPath.toString().split(".");
                    let currentMember: any = item;
                    for (const memberName of splitted) {
                        currentMember = currentMember[memberName]
                    }
                    return currentMember;
                }
                else {
                    return item[this.displayMemberPath];
                }
            }
        }
        return item;
    }

    public getValue(item: any): any {
        return this.selectedValuePath && item != null ? item[this.selectedValuePath] : item;
    }

    public selectedValueChanged(newValue: any, oldValue: any): void {
        if (newValue === oldValue || !this._isAttached) {
            return;
        }

        if (!this.filteredItemSource) {
            this.filteredItemSource = this.itemSource ? this.itemSource.slice(0) : this.itemSource;
        }

        if (this.itemSource) {
            this.selectedItem = Enumerable.from(this.itemSource).firstOrDefault(x => this.getValue(x) === newValue);
            this._indexFocusedLi = this.filteredItemSource.indexOf(this.selectedItem) % this.filteredItemSource.length;
        }
    }
 
    private get comboBoxName() : string {

        return this.identifier ?? this.label ?? "no_combobox_label"
    }
 
    private get selectedItemName() : string {
        return this.selectedItem?.displayName ?? this.selectedItem?.name ?? this.selectedValue;
    }

    public selectedItemChanged(newValue: any, oldValue: any): void {
        if (newValue === oldValue || !this._isAttached) {
            return;
        }
        
        this.selectedValue = this.getValue(newValue);
        this.eventAggregator.publish(Messages.comboSelected, {"comboBoxLabel" : this.comboBoxName, "itemSelected" : this.selectedItemName})
        this._indexFocusedLi = this.filteredItemSource.indexOf(this.selectedItem) % this.filteredItemSource.length;
        setTimeout(() => {
            const itemChangedEvent = new CustomEvent("selected-item-changed", { detail: { item: newValue, oldItem: oldValue }, bubbles: true });
            this.element.dispatchEvent(itemChangedEvent);
        });
    }

    public isEditingChanged(newValue: boolean): void {
        if (newValue) {
            this.focusTextBox(true);
            this.searchValue = this.getDisplayName(this.selectedItem);
            if (this.internalItemSource) {
                this.filteredItemSource = this.internalItemSource.toArray();
            }
        }
    }

    public focusTextBox(selectAlltext: boolean = false): void {
        setTimeout(() => {
            const input: HTMLInputElement = this.input;
            if (input) {
                input.focus();
                if (selectAlltext) {
                    input.select();
                }
            }
        });
    }

    public onFocus(): void {
        if(!this.readonly) {
            this.isEditing = true;
        }
    }

    public onBlurred(): void {
        if (!this.isMobile) {
            this.isDropdownOpen = false;
            this.isEditing = false;

            if (!this.showOnAdorner) {
                this.closePopup();
            }
        }
    }

    public onDropdownArrowMouseDown(): void {
        this.onFocus();
    }

    public onDropdownArrayMouseUp($event: Event): void {
        if(this.isDropdownOpen){
            // Prevents dropdown from re-opening after closing from the arrow button (triggered by blur event)
            $event.stopImmediatePropagation();
            // Defer closing popup to avoid window event conflicts
            setTimeout(()=>{ this.closePopup(); }, 200)
        }
    }

    public itemSourceChanged(newValue: any[], oldValue: any[]): void {

        if (JSON.stringify(newValue) == JSON.stringify(oldValue) || !this._isAttached) {
            return;
        }
        this.searchValue = "";

        if (newValue && this.selectedValue != undefined) {
            const selectedValueStringified = JSON.stringify(this.selectedValue);
            const condition = x => JSON.stringify(this.getValue(x)) == selectedValueStringified || this.getValue(x) === this.selectedValue;
            this.selectedItem = Enumerable.from(this.itemSource).firstOrDefault(condition);
        } else if (newValue && this.selectedItem) {
            const selectedItemStringified = JSON.stringify(this.selectedItem);
            const condition = x => JSON.stringify(x) == selectedItemStringified || x == this.selectedItem || this.getValue(x) === this.getValue(this.selectedItem);
            this.selectedValue = this.getValue(Enumerable.from(this.itemSource).firstOrDefault(condition));
        }
        const selectedItemStringified = JSON.stringify(this.selectedItem);
        const condition = x => JSON.stringify(x) == selectedItemStringified || x == this.selectedItem || (this.selectedItem && x[this.selectedValuePath] == this.selectedItem[this.selectedValuePath]);
        const itemExists = newValue ? newValue.filter(condition)[0] : null;
        if (this.setDefault && !itemExists && newValue) {
            if (typeof this.setDefault == "function") {
                const item = this.setDefault(this.internalItemSource.toArray());
                if (item) {
                    this.select(item);
                }
            } else {
                this.select(this.internalItemSource.firstOrDefault());
            }
        }
    }

    public searchValueChanged(newValue: string): void {
        this.applySearchFilter(newValue);
    }

    applySearchFilter(newValue: string) {
        if (this.internalItemSource) {
            if (newValue != null && newValue.length > 0) {
                this.filteredItemSource = this.internalItemSource
                    .where(
                        x => {
                            const displayName = this.getDisplayName(x);

                            return displayName && displayName.toString().toLowerCase().indexOf(newValue.toString().toLowerCase()) !== -1
                        }
                    ).toArray();
            } else {
                this.filteredItemSource = this.internalItemSource.toArray();
            }
        }
    }

    public select = (item: any): void => {
        if (item != null) {
            this.selectedItem = item;
            this.selectedValue = this.getValue(item);
            this.isEditing = false;
            this.isDropdownOpen = false;
            this.onSelect();
        }
    }

    public onSelect(): void {
        setTimeout(() => {
            const paramsEvent: any = { detail: this.selectedItem, bubbles: true };
            const changeEvent: CustomEvent = new CustomEvent("item-selected", paramsEvent);
            this.element.dispatchEvent(changeEvent);
        });
    }

    //Open the combobox/display items
    public open(event: Event): void {
        if (!this.disabled && !this.readonly && !this.isDropdownOpen) {
            if (!(<any>event).propagationStopped) {
                this.eventAggregator.publish(Messages.documentClick, {});
            }
            event.stopPropagation();

            this.isEditing = !this.isEditing;
            this.isDropdownOpen = true;

            const internalItemSource = this.internalItemSource.toArray();

            this._indexFocusedLi = internalItemSource.indexOf(this.selectedItem) % internalItemSource.length;
        }
    }

    public setDefaultChanged(): void {
        const source = this.internalItemSource;
        if (this.setDefault && source && (this.selectedItem == null) && (this.selectedValue == null)) {
            if (typeof this.setDefault == "function") {
                const item = this.setDefault(source.toArray());
                if (item) {
                    this.select(item);
                }
            } else {
                this.select(source.firstOrDefault());
            }
        }
    }

    stopPropagation = (e: Event): void => {
        e.stopPropagation();
    }

    closePopup = () => {
        this.isEditing = false;
        this.isDropdownOpen = false;
    }

    private getTop(elem: HTMLElement): number {
        if (!elem || !elem.getBoundingClientRect) {
            return 0;
        }

        const rect = elem.getBoundingClientRect();
        const doc = elem.ownerDocument;
        const docElem = doc.documentElement;
        const win = doc.defaultView;
        return rect.top + win.pageYOffset - docElem.clientTop;
    }

    private onKeyDown = (event: KeyboardEvent): void => {
        if (!this.disabled && !this.readonly) {
            this.isDropdownOpen = true;
            this.onKeyDownNavigation(event);
        }
    }
}

export class DropDownContext {
    constructor(public comboBoxContext: ComboBox) { }

    @computedFrom(Reflection.getPropertyPath<DropDownContext>(z => z.comboBoxContext.isDropdownOpen))
    get isDropdownOpen(): boolean {
        return this.comboBoxContext && this.comboBoxContext.isDropdownOpen;
    }
}
